/* hooks/useFinancialData.ts
 * Builds every financial-data artefact used inside the Financial-Information tab.
 * Fully compatible with the 18-month fiscal-key grid (Feb-25 → Jul-26).
 */

import {useMemo} from 'react';
import {IFdApplicationType} from 'constants/interfaces';

import * as calc from '../utils/financialCalculations';
import {MONTHS} from '../utils/financialCalculations';
import {lshParseOperatingCostMonthlyCosts} from '../utils/operatingCostMonthlyCosts';

import {
  GridRow,
  FiscalKey,
  BLANK as BLANK_FISCAL,
} from '../cards/OperatingCostsCard/OperatingCostsGrid';

import {buildMiniPLData} from '../utils/financialHistory';

/* ───────── helper types ───────── */
export type RevenueRow = GridRow & {cropName?: string};

/* ───────── helpers ───────── */
const cloneBlank = (): Record<FiscalKey, number> => ({...BLANK_FISCAL});

const fiscalKey = (d: Date): FiscalKey =>
  `${MONTHS[d.getMonth()]}${String(d.getFullYear()).slice(-2)}` as FiscalKey;

/* ───────── main hook ───────── */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function useFinancialData(app?: IFdApplicationType) {
  /* 1️⃣  forward-looking mini-P&L projection (backend util) */
  const projection = useMemo(() => calc.buildFinancialProjection(app), [app]);

  /* 2️⃣  historic mini-P&L (backend + cost rows) */
  const {years: historyYears, data: historyDataRaw} = useMemo(
    () => buildMiniPLData(app),
    [app],
  );

  /* 3️⃣  overlay current-season projection onto historic object */
  const historyData = useMemo(() => {
    const out = {...historyDataRaw};

    projection.forEach((p) => {
      out[p.year] = {
        ...out[p.year],
        revenue: p.revenue,
        grossProfit: p.grossProfit,
        costOfSales: p.costOfSales,
        /* use operatingCosts from projection data */
        operatingCosts: p.operatingCosts || 0,
        netProfit: p.netProfit,
      };
    });

    return out;
  }, [historyDataRaw, projection]);

  /* 4️⃣  crop-level cards (per-crop profitability) */
  const cropCards = useMemo(() => {
    if (!app) return [];

    /* use the fiscal start-year so it auto-advances next season */
    const currentYear = 2025;

    const thisYearsCosts =
      app.productionCosts?.filter((c) => c.year === currentYear) ?? [];

    return (app.productionPlans ?? []).map((p) => {
      const costs = thisYearsCosts.find((c) => c.crop?.id === p.crop?.id);

      const revenue =
        (p.numberOfHectares || 0) *
        (p.estimatedYield || 0) *
        (p.estimatedPrice || 0);

      const seed = costs?.seed ?? 0;
      const fert = costs?.fertiliser ?? 0;
      const chem = costs?.chemical ?? 0;

      return {
        crop: p.crop,
        hectares: p.numberOfHectares,
        revenue,
        grossProfit: revenue - (seed + fert + chem),
        costs: {
          id: costs?.id ?? '',
          seed,
          fertilizer: fert,
          chemical: chem,
        },
      };
    });
  }, [app]);

  /* 5️⃣  operating-cost doughnut */
  const opCost = useMemo(() => {
    if (!app) return {total: 0, categories: []};

    const costs2025 = app.operatingCosts?.filter((c) => c.year === 2025) ?? [];
    const total = calc.getTotalOperatingCosts(costs2025);

    return {
      total,
      categories: costs2025.map((c) => ({
        name: c.expense?.name ?? '',
        value: lshParseOperatingCostMonthlyCosts(c.monthlyCosts).total,
      })),
    };
  }, [app]);

  /* 6️⃣  revenue rows feeding the cash-flow grid */
  const revenueRows = useMemo<RevenueRow[]>(() => {
    if (!app) return [];
    const rows: RevenueRow[] = [];

    (app.productionPlans ?? []).forEach((p) => {
      const rev =
        (p.numberOfHectares || 0) *
        (p.estimatedYield || 0) *
        (p.estimatedPrice || 0);

      if (!rev || !p.plantingDate) return;

      /* harvest ≈ 9 months after planting */
      const harvest = new Date(p.plantingDate);
      harvest.setMonth(harvest.getMonth() + 9);

      const key = fiscalKey(harvest);
      if (!(key in BLANK_FISCAL)) return;

      const mv = cloneBlank();
      mv[key] = rev;

      rows.push({
        ...mv,
        rowKind: 'revenue',
        expenseId: 0,
        label: `${p.crop?.name ?? 'Crop'} Revenue`,
        cropName: p.crop?.name ?? '',
      } as RevenueRow);
    });

    /* funding-ask draw-down (Aug-25) */
    const fundingTotal = (app.fundingAsk?.fundingRequestCosts ?? []).reduce(
      (s, c) => s + (c?.cost || 0),
      0,
    );

    if (fundingTotal) {
      const mv = cloneBlank();
      mv.aug25 = fundingTotal;

      rows.push({
        ...mv,
        rowKind: 'revenue',
        expenseId: 0,
        label: 'Funding Ask Draw-down',
        cropName: '',
      } as RevenueRow);
    }

    return rows;
  }, [app]);

  /* ───────── exports ───────── */
  return {
    projection,
    cropCards,
    opCost,
    revenueRows,
    historyYears,
    historyData,
  };
}
